import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
} from 'react';
import { observer } from 'mobx-react';
import { DialogActions, DialogContent, Divider } from '@mui/material';
import styled from 'styled-components';
import Dialog from 'components/UI/Dialog';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import Space from 'components/UI/Space';
import { DialogOnCloseReason } from 'models/shared';
import { VpdThreshold } from 'models/restTypes';
import api from 'Api/ApiMethods';
import { useStores } from 'stores/hooks/hooks';

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
`;

interface VpdThresholdEditDialogProps {
  onHide: () => void;
  visible: boolean;
  threshold: VpdThreshold;
  onUpdated: (threshold: VpdThreshold) => void;
}

const VpdThresholdEditDialog: FC<VpdThresholdEditDialogProps> = observer(
  ({ visible = false, onHide, threshold, onUpdated }) => {
    const { t } = useTranslation('thresholds');
    const { snackBarStore } = useStores();
    const { t: generalT } = useTranslation('general');

    const [thresholdEdit, setThresholdEdit] = useState<VpdThreshold>(() =>
      JSON.parse(JSON.stringify(threshold))
    );

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
      setThresholdEdit(JSON.parse(JSON.stringify(threshold)));
    }, [threshold]);

    const closeDialog = useCallback(() => {
      onHide();
    }, [onHide]);

    const handleCloseDialog = useCallback(
      (event: any, reason: DialogOnCloseReason) => {
        if (reason && reason === DialogOnCloseReason.BackDropClick) {
          return;
        }

        closeDialog();
      },
      [closeDialog],
    );

    const handleCloseButton = useCallback(() => {
      closeDialog();
    }, [closeDialog]);

    const handleNumberChange = useCallback(
      (field: keyof VpdThreshold['detectionParams']) =>
        (event: ChangeEvent<HTMLInputElement>) => {
          const { value } = event.target;
          const parsed = Number(value);
          setThresholdEdit((prev) => ({
            ...prev,
            detectionParams: {
              ...prev.detectionParams,
              [field]: value === '' || Number.isNaN(parsed) ? null : parsed,
            },
          }));
        },
      [],
    );

    const handleSave = useCallback(async () => {
      setIsSubmitting(true);
      try {
        await api.putVpdThreshold(thresholdEdit);
        onUpdated(thresholdEdit);
        snackBarStore.showToast({
          detail: t('edit_vpd_success'),
          severity: 'success',
          summary: 'Success',
        });

        closeDialog();
      } catch (error) {
        snackBarStore.showToast({
          detail: t('edit_vpd_failure'),
          severity: 'error',
          summary: 'Error',
        });

        console.error('Failed to update VPD threshold:', error);
      } finally {
        setIsSubmitting(false);
      }
    }, [thresholdEdit, onUpdated, closeDialog, snackBarStore, t]);

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        dialogTitle={`${t('edit_vpd_threshold')} - ${t('crop_id')}: ${threshold.cropId
          }`}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <DialogContent>
          <Space height={1} />
          <Grid direction="column" gap="10px">
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('crop_id')}:</NoWrappingText>
              <Input
                name="cropId"
                label={t('crop_id')}
                id="cropId"
                value={thresholdEdit.cropId}
                variant="outlined"
                type="number"
                fullWidth
                disabled
              />
            </Grid>

            {/* <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('mds_min')}:</NoWrappingText>
              <Input
                name="MdsMin"
                label={t('mds_min')}
                id="MdsMin"
                value={thresholdEdit.detectionParams.MdsMin ?? ''}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange('MdsMin')}
                inputProps={{ step: '0.001' }}
              />
            </Grid> */}

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('vpd_max')}:</NoWrappingText>
              <Input
                name="VPDmax"
                label={t('vpd_max')}
                id="VPDmax"
                value={thresholdEdit.detectionParams.VPDmax ?? ''}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange('VPDmax')}
                inputProps={{ step: '0.1' }}
              />
            </Grid>

            {/* <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('mds_rate')}:</NoWrappingText>
              <Input
                name="MDSrate"
                label={t('mds_rate')}
                id="MDSrate"
                value={thresholdEdit.detectionParams.MDSrate ?? ''}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange('MDSrate')}
                inputProps={{ step: '0.1' }}
              />
            </Grid> */}

            {/* <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('pmb_delta')}:</NoWrappingText>
              <Input
                name="PMBDelta"
                label={t('pmb_delta')}
                id="PMBDelta"
                value={thresholdEdit.detectionParams.PMBDelta ?? ''}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange('PMBDelta')}
                inputProps={{ step: '0.001' }}
              />
            </Grid> */}

            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('vpd_night_max')}:</NoWrappingText>
              <Input
                name="VPDNIGHTmax"
                label={t('vpd_night_max')}
                id="VPDNIGHTmax"
                value={thresholdEdit.detectionParams.VPDNIGHTmax ?? ''}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange('VPDNIGHTmax')}
                inputProps={{ step: '0.1' }}
              />
            </Grid>
            {/* 
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('pmb_reduction')}:</NoWrappingText>
              <Input
                name="PMBreduction"
                label={t('pmb_reduction')}
                id="PMBreduction"
                value={thresholdEdit.detectionParams.PMBreduction ?? ''}
                variant="outlined"
                type="number"
                fullWidth
                onChange={handleNumberChange('PMBreduction')}
                inputProps={{ step: '0.01' }}
              />
            </Grid> */}
          </Grid>
          <Space height={1} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Text textcolor={theme.color.white} size="sm">
              {generalT('save')}
            </Text>
          </Button>
          <Button onClick={handleCloseButton}>
            <Text textcolor={theme.color.white} size="sm">
              {generalT('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default VpdThresholdEditDialog;
